import { rem } from 'polished';
import { css } from 'styled-components';

import type { Spacing, Theme } from '../theme/types';
import { isSpacingKey } from '../theme/utils';
import type { Styled } from '../types/common.types';

type SpacingPropValue = number | undefined | keyof Spacing | (string & {});

export type BaseSpacingProps = {
  /** padding  */
  p: SpacingPropValue;
  /** padding-top */
  pt: SpacingPropValue;
  /** padding-right */
  pr: SpacingPropValue;
  /** padding-bottom */
  pb: SpacingPropValue;
  /** padding-left */
  pl: SpacingPropValue;

  /** margin */
  m: SpacingPropValue;
  /** margin-top */
  mt: SpacingPropValue;
  /** margin-right */
  mr: SpacingPropValue;
  /** margin-bottom */
  mb: SpacingPropValue;
  /** margin-left */
  ml: SpacingPropValue;
};

export type SpacingProps = Partial<BaseSpacingProps>;

export type StyledSpacingProps = Styled<SpacingProps>;

const spacingValue = (
  theme: Theme,
  value: SpacingPropValue,
): string | undefined => {
  if (typeof value === 'number') {
    return rem(value);
  }

  if (!value) return undefined;

  if (isSpacingKey(theme, value)) {
    return theme.core.spacing[value];
  }

  return value;
};

/**
 * Styles that can map spacing props prefixed with `$` to an adequate CSS property
 *
 * @example
 * const Test = styled.div<StyledSpacingProps>`
 *   ${spacingStyles}
 * `
 */
export const spacingStyles = css<StyledSpacingProps>(
  ({ theme, $p, $pt, $pr, $pb, $pl, $m, $mt, $mr, $mb, $ml }) => ({
    padding: spacingValue(theme, $p),
    paddingTop: spacingValue(theme, $pt),
    paddingRight: spacingValue(theme, $pr),
    paddingBottom: spacingValue(theme, $pb),
    paddingLeft: spacingValue(theme, $pl),
    margin: spacingValue(theme, $m),
    marginTop: spacingValue(theme, $mt),
    marginRight: spacingValue(theme, $mr),
    marginBottom: spacingValue(theme, $mb),
    marginLeft: spacingValue(theme, $ml),
  }),
);

export const extractSpacingProps = <T extends SpacingProps>(
  props: T,
): BaseSpacingProps => {
  const { p, pt, pr, pb, pl, m, mt, mr, mb, ml } = props;

  const extracted: BaseSpacingProps = { p, pt, pr, pb, pl, m, mt, mr, mb, ml };

  return extracted;
};
